import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

const WA_NUMBER = '918320206804'; // country code + number, no +

const buildWaUrl = (text) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

const useWhatsAppMessage = () => {
  const location = useLocation();
  const [message, setMessage] = useState('Hi! I have a question about Skinn Thrive.');

  useEffect(() => {
    const path = location.pathname;

    // Order success — pre-fill order number
    const orderMatch = path.match(/^\/order-success\/(.+)/);
    if (orderMatch) {
      setMessage(`Hi! I just placed an order (${orderMatch[1]}) and need help.`);
      return;
    }

    // Product detail — pick title from page <title> tag (fastest, no extra fetch)
    if (path.startsWith('/product/')) {
      const title = document.title.replace(' — Skinn Thrive', '').trim();
      if (title && title !== 'Skinn Thrive') {
        setMessage(`Hi! I'd like to know more about "${title}".`);
      } else {
        setMessage("Hi! I have a question about a product on Skinn Thrive.");
      }
      return;
    }

    // Cart
    if (path === '/cart') {
      setMessage("Hi! I need help with my cart or an order I'm placing.");
      return;
    }

    // Track order
    if (path.startsWith('/track-order')) {
      setMessage("Hi! I need help tracking my Skinn Thrive order.");
      return;
    }

    // Account / orders
    if (path.startsWith('/account')) {
      setMessage("Hi! I have a question about my Skinn Thrive account or order.");
      return;
    }

    // Default
    setMessage("Hi! I have a question about Skinn Thrive.");
  }, [location.pathname]);

  return message;
};

const WhatsAppButton = () => {
  const message = useWhatsAppMessage();
  const [visible, setVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Fade in after 1.5s so it doesn't distract on first load
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // Show the tooltip once per visit (not on every page), briefly — it used to cover product buttons on phones
  useEffect(() => {
    if (!visible) return undefined;
    let seen = false;
    try { seen = sessionStorage.getItem('st_wa_tip') === '1'; sessionStorage.setItem('st_wa_tip', '1'); } catch { /* private mode */ }
    if (seen) return undefined;
    let hide;
    const t = setTimeout(() => {
      setShowTooltip(true);
      hide = setTimeout(() => setShowTooltip(false), 3500);
    }, 800);
    return () => { clearTimeout(t); clearTimeout(hide); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 right-3 md:bottom-6 md:right-6 z-30 flex flex-col items-end gap-2">
      {/* Tooltip bubble */}
      {showTooltip && (
        <div className="relative animate-fade-in bg-white text-ink text-xs font-medium px-3.5 py-2 rounded-xl shadow-card border border-border whitespace-nowrap">
          Need help? Chat with us
          <div className="absolute bottom-[-6px] right-5 w-3 h-3 bg-white border-r border-b border-border rotate-45" />
        </div>
      )}

      {/* WhatsApp button */}
      <a
        href={buildWaUrl(message)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        title="Chat on WhatsApp"
        className="group relative flex items-center justify-center w-12 h-12 md:w-[52px] md:h-[52px] rounded-full shadow-lg shadow-black/10 transition-transform duration-200 hover:scale-105 active:scale-95"
        style={{ backgroundColor: '#25D366' }}
        onClick={() => setShowTooltip(false)}
      >

        {/* WhatsApp SVG icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          className="w-6 h-6 md:w-7 md:h-7 relative z-10"
          fill="white"
        >
          <path d="M16.002 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.363.638 4.67 1.848 6.688L2.667 29.333l6.813-1.786A13.288 13.288 0 0016.002 29.333c7.364 0 13.331-5.969 13.331-13.333S23.366 2.667 16.002 2.667zm0 24.267a11 11 0 01-5.617-1.544l-.403-.24-4.043 1.06 1.08-3.94-.262-.417A11.002 11.002 0 015.002 16c0-6.065 4.935-11 11-11s11 4.935 11 11-4.935 11-11 11zm6.03-8.233c-.33-.165-1.952-.963-2.255-1.073-.303-.11-.523-.165-.743.165-.22.33-.852 1.073-1.044 1.293-.193.22-.385.248-.715.083-.33-.165-1.394-.514-2.655-1.638-.98-.875-1.643-1.956-1.835-2.286-.193-.33-.02-.508.145-.672.149-.148.33-.385.495-.578.165-.193.22-.33.33-.55.11-.22.055-.413-.028-.578-.083-.165-.743-1.79-1.018-2.452-.268-.645-.54-.557-.743-.567l-.633-.012a1.213 1.213 0 00-.88.413c-.303.33-1.155 1.128-1.155 2.75s1.183 3.19 1.348 3.41c.165.22 2.328 3.556 5.643 4.988.788.34 1.404.543 1.883.695.792.252 1.513.216 2.083.131.635-.095 1.952-.798 2.228-1.569.275-.77.275-1.43.193-1.568-.083-.138-.303-.22-.633-.385z" />
        </svg>
      </a>
    </div>
  );
};

export default WhatsAppButton;
